import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, User, LogOut, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const MarketingLayout = ({ children, lightTheme = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: <>TryOn Studio<sup className="text-[10px] align-super">™</sup></>, href: '/tryon-studio' },
    { name: 'Solutions', href: '/solutions' },
    { name: 'Why Nanokart', href: '/why-nanokart' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Developer Portal', href: '/developer' },
  ];

  // Check if a nav link is active
  const isLinkActive = (href) => {
    const currentPath = location.pathname;
    const currentHash = location.hash;
    
    // For hash links on home page (e.g., /#features, /#pricing)
    if (href.startsWith('/#')) {
      return currentPath === '/' && currentHash === href.substring(1);
    }
    
    // For regular page links
    return currentPath === href;
  };

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  const getDashboardPath = () => {
    if (user?.is_admin) return '/admin';
    return '/partners';
  };

  const getUserDisplayName = () => {
    if (user?.business_name) return user.business_name;
    if (user?.contact_name) return user.contact_name;
    if (user?.email) return user.email.split('@')[0];
    return 'My Account';
  };

  // Premium Blue theme styles
  const styles = {
    bg: 'bg-white',
    headerBg: 'bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm',
    navText: 'text-slate-600 hover:text-blue-600',
    logoText: 'text-slate-900',
    gradientText: 'text-blue-600',
    footerBorder: 'border-slate-200',
    footerText: 'text-slate-500',
    footerHeading: 'text-slate-900',
    mobileMenuBg: 'bg-white border-b border-slate-200',
  };

  return (
    <div className={`min-h-screen ${styles.bg}`}>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50">
        <div className={styles.headerBg}>
          <div className="container mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between h-16 sm:h-20">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="/new-logo.png" 
                  alt="Nanokart.ai" 
                  className="h-12 sm:h-16 w-auto"
                />
              </Link>

              {/* Desktop Nav */}
              <nav className="hidden md:flex items-center gap-8">
                {navLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    className={`transition-colors text-sm font-medium ${
                      isLinkActive(link.href)
                        ? 'text-blue-600 border-b-2 border-blue-600 pb-1'
                        : `${styles.navText}`
                    }`}
                  >
                    {link.name}
                  </a>
                ))}
              </nav>

              {/* Auth Buttons */}
              <div className="hidden md:flex items-center gap-3">
                {isAuthenticated ? (
                  <>
                    <Link 
                      to={getDashboardPath()} 
                      className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      {user?.is_admin ? 'Admin Panel' : 'Dashboard'}
                    </Link>
                    <div className="relative">
                      <button
                        onClick={() => setUserMenuOpen(!userMenuOpen)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <span className="text-sm font-medium text-slate-700 max-w-[120px] truncate">
                          {getUserDisplayName()}
                        </span>
                      </button>
                      
                      {/* User Dropdown */}
                      <AnimatePresence>
                        {userMenuOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50"
                          >
                            <Link
                              to={getDashboardPath()}
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                            >
                              <LayoutDashboard className="w-4 h-4" />
                              {user?.is_admin ? 'Admin Panel' : 'Dashboard'}
                            </Link>
                            <button
                              onClick={handleLogout}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                            >
                              <LogOut className="w-4 h-4" />
                              Sign Out
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </>
                ) : (
                  <>
                    <Link 
                      to="/partners/login" 
                      className={`${styles.navText} transition-colors text-sm font-medium px-4 py-2`}
                    >
                      Sign In
                    </Link>
                    <Link 
                      to="/partners/register" 
                      className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
                    >
                      Get Started
                    </Link>
                  </>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`md:hidden p-2 ${styles.navText}`}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`md:hidden ${styles.mobileMenuBg}`}
            >
              <div className="container mx-auto px-6 py-4">
                <nav className="flex flex-col gap-4">
                  {navLinks.map((link) => (
                    <a
                      key={link.name}
                      href={link.href}
                      className={`transition-colors py-2 ${
                        isLinkActive(link.href)
                          ? 'text-blue-600 font-semibold border-l-4 border-blue-600 pl-3'
                          : `${styles.navText}`
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.name}
                    </a>
                  ))}
                  <hr className={styles.footerBorder} />
                  {isAuthenticated ? (
                    <>
                      <div className="flex items-center gap-2 py-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <span className="text-sm font-medium text-slate-700">
                          {getUserDisplayName()}
                        </span>
                      </div>
                      <Link
                        to={getDashboardPath()}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold text-center flex items-center justify-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        {user?.is_admin ? 'Admin Panel' : 'Dashboard'}
                      </Link>
                      <button
                        onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                        className="px-6 py-3 border border-red-200 text-red-600 rounded-lg font-semibold text-center flex items-center justify-center gap-2"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/partners/login"
                        className={`${styles.navText} transition-colors py-2`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Sign In
                      </Link>
                      <Link
                        to="/partners/register"
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold text-center"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Get Started
                      </Link>
                    </>
                  )}
                </nav>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className={`border-t ${styles.footerBorder} py-12 bg-slate-50`}>
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-10">
            {/* Brand */}
            <div className="md:col-span-1">
              <Link to="/" className="flex items-center gap-2 mb-4">
                <img 
                  src="/new-logo.png" 
                  alt="Nanokart.ai" 
                  className="h-24 w-auto"
                />
              </Link>
              <p className={`${styles.footerText} text-sm leading-relaxed`}>
                Conversion & return optimization for fashion brands.
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className={`font-semibold mb-4 text-sm ${styles.footerHeading}`}>Product</h4>
              <ul className={`space-y-2.5 ${styles.footerText} text-sm`}>
                <li><Link to="/tryon-studio" className="hover:text-blue-600 transition-colors">TryOn Studio<sup className="text-[8px]">™</sup></Link></li>
                <li><Link to="/solutions" className="hover:text-blue-600 transition-colors">Solutions</Link></li>
                <li><Link to="/why-nanokart" className="hover:text-blue-600 transition-colors">Why Nanokart</Link></li>
                <li><a href="/#pricing" className="hover:text-blue-600 transition-colors">Pricing</a></li>
                <li><Link to="/docs" className="hover:text-blue-600 transition-colors">API Docs</Link></li>
                <li><Link to="/request-access" className="hover:text-blue-600 transition-colors">Request Access</Link></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className={`font-semibold mb-4 text-sm ${styles.footerHeading}`}>Company</h4>
              <ul className={`space-y-2.5 ${styles.footerText} text-sm`}>
                <li><Link to="/about" className="hover:text-blue-600 transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-blue-600 transition-colors">Contact</Link></li>
                <li><a href="#" className="hover:text-blue-600 transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-blue-600 transition-colors">Careers</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className={`font-semibold mb-4 text-sm ${styles.footerHeading}`}>Legal</h4>
              <ul className={`space-y-2.5 ${styles.footerText} text-sm`}>
                <li><Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-blue-600 transition-colors">Terms of Service</Link></li>
                <li><Link to="/refunds" className="hover:text-blue-600 transition-colors">Refund Policy</Link></li>
              </ul>
            </div>
          </div>

          <div className={`border-t ${styles.footerBorder} mt-10 pt-8`}>
            {/* Trust Elements */}
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-6">
              <span className={`${styles.footerText} text-xs`}>🇮🇳 Built for Indian fashion sellers</span>
              <span className={`${styles.footerText} text-xs`}>• Data hosted in India</span>
              <span className={`${styles.footerText} text-xs`}>• DPIIT Recognized Startup</span>
            </div>
            
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className={`${styles.footerText} text-sm`}>
                © {new Date().getFullYear()} NanoKart India Private Limited. All rights reserved.
              </p>
              <div className="flex items-center gap-6">
                <a href="#" className={`${styles.footerText} hover:text-blue-600 transition-colors text-sm`}>
                  Twitter
                </a>
                <a href="#" className={`${styles.footerText} hover:text-blue-600 transition-colors text-sm`}>
                  LinkedIn
                </a>
                <a href="#" className={`${styles.footerText} hover:text-blue-600 transition-colors text-sm`}>
                  GitHub
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MarketingLayout;
